import { CpfInput } from '@/_components/inputs/cpf-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/_components/ui'
import type { UpdateGuarantorSchema } from '@/_schemas/update-guarantor'
import { AccessControl } from '@/features/_components/access-control'
import { ContentContainer } from '@/features/_components/content-container'
import { InputGroup } from '@/features/_components/input-group'
import { Text, Title } from '@/features/_components/ui/typography'
import { useSession } from '@/features/_hooks/use-session'
import { mutations } from '@/features/_lib/react-query'
import type { TExposedPersonAggregateDTO } from '@doutorsim/shared/persons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, X } from 'lucide-react'
import { useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CiCircleAlert } from 'react-icons/ci'
import { toast } from 'sonner'
import { EditContext } from '../../../_providers/edit'

interface GuarantorProfileFormProps {
  guarantor: TExposedPersonAggregateDTO
  companyId: string
}

export const GuarantorProfileForm = ({ guarantor, companyId }: GuarantorProfileFormProps) => {
  const form = useFormContext<UpdateGuarantorSchema>()
  const [isPending, setIsPending] = useState(false)
  const session = useSession()

  const queryClient = useQueryClient()
  const updateGuarantorMutation = useMutation(mutations.persons.update(queryClient))

  const { isFormEditable, isCancelDialogOpen, setIsCancelDialogOpen, setIsFormEditable } =
    useContext(EditContext)

  const onSubmit = async (formData: UpdateGuarantorSchema) => {
    setIsPending(true)

    try {
      const updatedPerson = await updateGuarantorMutation.mutateAsync({
        id: guarantor.id,
        companyId,
        name: formData.name ?? guarantor.name,
        cpf: formData.cpf ?? guarantor.cpf,
        email: formData.email ?? guarantor.email,
        phone: formData.phone ?? guarantor.phone,
        birthDate: guarantor.birthDate,
        motherName: guarantor.motherName,
        estimatedIncome: guarantor.estimatedIncome,
        address: guarantor.address,
      })

      setIsFormEditable(false)
      form.reset({
        cpf: updatedPerson.cpf,
        email: updatedPerson.email ?? undefined,
        name: updatedPerson.name,
        phone: updatedPerson.phone ?? undefined,
      })
      toast.success('Avalista atualizado com sucesso!')
    } catch {
      toast.error('Erro ao atualizar avalista')
    } finally {
      setIsPending(false)
    }
  }

  const handleCancelClick = () => setIsCancelDialogOpen(true)

  const resetForm = () => {
    form.reset()
    setIsCancelDialogOpen(false)
    setIsFormEditable(false)
  }

  const isFormModified = Object.keys(form.formState.dirtyFields).length > 0

  const buttonDisabledStates =
    !isFormModified || form.formState.isSubmitting || !isFormEditable || isPending

  return (
    <div className="scrollbar w-full overflow-y-auto">
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="min-w-[336px] max-w-[389px] p-5">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-1">
                <CiCircleAlert size={24} />
                <Title className="text-base">Há alterações não salvas!</Title>
              </div>
              <Text>Ao prosseguir, essas alterações serão perdidas. Deseja continuar?</Text>
            </div>

            <div className="flex gap-3 self-end">
              <DialogClose asChild>
                <Button variant="gray" className="rounded-md font-bold">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                className="min-w-[80px] text-sm font-bold"
                onClick={resetForm}
              >
                Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Form {...form}>
        <form
          className="scrollbar flex w-full flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <ContentContainer>
            <div className="flex items-center justify-between">
              <h2 className="font-bold">Identificação</h2>
              <AccessControl
                role={session.data?.user.userType}
                renderIfAdmin={
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      if (isFormEditable && isFormModified) {
                        return setIsCancelDialogOpen(true)
                      }
                      setIsFormEditable(!isFormEditable)
                    }}
                  >
                    {isFormEditable ? (
                      <>
                        <X size={12} />
                        Cancelar
                      </>
                    ) : (
                      <>
                        <Pencil size={12} />
                        Editar
                      </>
                    )}
                  </button>
                }
                renderIfUser={null}
              />
            </div>
            <InputGroup>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Nome completo</FormLabel>
                    <FormControl>
                      <Input placeholder="Digite seu nome completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="cpf"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>CPF</FormLabel>
                    <FormControl>
                      <CpfInput placeholder="Digite o CPF do avalista" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
            <InputGroup>
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Número de celular</FormLabel>
                    <FormControl>
                      <PhoneInput
                        placeholder="Digite o telefone do avalista"
                        disabled={!isFormEditable}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        value={field.value}
                        getInputRef={field.ref}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="Digite o e-mail do avalista" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </InputGroup>
          </ContentContainer>
          <AccessControl
            role={session.data?.user.userType}
            renderIfAdmin={
              <div className="flex justify-end gap-[10px]">
                <Button
                  variant="gray"
                  className="font-bold"
                  type="button"
                  disabled={buttonDisabledStates}
                  onClick={handleCancelClick}
                >
                  Cancelar
                </Button>
                <Button type="submit" isLoading={isPending} disabled={buttonDisabledStates}>
                  Salvar
                </Button>
              </div>
            }
            renderIfUser={null}
          />
        </form>
      </Form>
    </div>
  )
}
